import { Grid, makeStyles, TextField } from '@freighthub/silhouette-core'
import { max } from 'lodash-es'
import { FC, useEffect, useMemo } from 'react'
import { useController } from 'react-hook-form'
import { useI18n } from 'utils/localisation'

import { calculateVolumeInCubicMeters, QuoteRequestFormState } from '../quoteRequestUtils'
import { CommodityAutocomplete } from './CommodityAutocomplete'
import { DeleteRow } from './DeleteRow'

const minimumAllowedNumberOfPieces = 1
const minimumAllowedDimensionValue = 0.1
const minimumAllowedWeightPerPiece = 0.001

const useStyles = makeStyles((theme) => ({
  cargoRow: {
    padding: theme.spacing(2, 0, 1),
    marginTop: theme.spacing(1),
  },
}))

type LclCargoRowProps = {
  index: number
  deleteCargo: (() => void) | null
}

export type LeanLclCargoRowProps = Omit<LclCargoRowProps, 'index'> & {
  quantity: number
  length: number
  width: number
  height: number
  grossWeight: number
  commodity: string
  volume: number
  onVolumeChange: (volume: number) => void
  onLengthChange: (length: number) => void
  onWidthChange: (width: number) => void
  onHeightChange: (height: number) => void
  onQuantityChange: (quantity: number) => void
  onGrossWeightChange: (grossWeight: number) => void
  onCommodityChange: (commodity: string) => void
}
export const LeanLCLCargoRow: FC<LeanLclCargoRowProps> = ({
  deleteCargo,
  quantity,
  onQuantityChange,
  length,
  onLengthChange,
  width,
  onWidthChange,
  height,
  onHeightChange,
  grossWeight,
  onGrossWeightChange,
  commodity,
  onCommodityChange,
  volume,
  onVolumeChange,
}) => {
  const classes = useStyles()
  const { translate } = useI18n()

  const newVolume = useMemo(
    () =>
      calculateVolumeInCubicMeters({
        length,
        width,
        height,
      }),
    [length, width, height]
  )

  useEffect(() => {
    if (volume !== newVolume) {
      onVolumeChange(newVolume)
    }
  }, [volume, newVolume])

  return (
    <Grid data-testid="lcl_cargo-row" container wrap="nowrap" spacing={1} className={classes.cargoRow}>
      <Grid xs={3}>
        <TextField
          label={translate('lcl_quote_request-cargo-quantity')}
          required
          fullWidth
          type="number"
          inputProps={{
            min: minimumAllowedNumberOfPieces,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          value={quantity || ''}
          onChange={(e) => onQuantityChange(max([Number(e.target.value), minimumAllowedNumberOfPieces]) as number)}
        />
      </Grid>
      <Grid xs={2}>
        <TextField
          required
          fullWidth
          helperText="cm"
          label={translate('quote_request-cargo-length')}
          type="number"
          inputProps={{
            min: minimumAllowedDimensionValue,
            step: 0.1,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          value={length || ''}
          onChange={(e) => onLengthChange(max([Number(e.target.value), minimumAllowedDimensionValue]) as number)}
        />
      </Grid>
      <Grid xs={2}>
        <TextField
          required
          fullWidth
          helperText="cm"
          label={translate('quote_request-cargo-width')}
          type="number"
          inputProps={{
            min: minimumAllowedDimensionValue,
            step: 0.1,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          value={width || ''}
          onChange={(e) => onWidthChange(max([Number(e.target.value), minimumAllowedDimensionValue]) as number)}
        />
      </Grid>
      <Grid xs={2}>
        <TextField
          required
          fullWidth
          helperText="cm"
          label={translate('quote_request-cargo-height')}
          type="number"
          inputProps={{
            min: minimumAllowedDimensionValue,
            step: 0.1,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          value={height || ''}
          onChange={(e) => onHeightChange(max([Number(e.target.value), minimumAllowedDimensionValue]) as number)}
        />
      </Grid>
      <Grid xs={2}>
        <TextField
          disabled
          fullWidth
          style={{ opacity: 0.5 }}
          label={translate('quote_request-cargo-volume')}
          helperText="cbm"
          type="number"
          inputProps={{
            min: 0,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          value={volume || ''}
        />
      </Grid>
      <Grid xs={3}>
        <TextField
          fullWidth
          helperText="kg"
          InputLabelProps={{
            shrink: true,
          }}
          label={translate('quote_request-cargo-weight')}
          type="number"
          inputProps={{
            min: minimumAllowedWeightPerPiece,
            step: 0.001,
          }}
          required
          value={grossWeight || ''}
          onChange={(e) => onGrossWeightChange(max([Number(e.target.value), minimumAllowedWeightPerPiece]) as number)}
        />
      </Grid>
      <Grid xs={3}>
        <CommodityAutocomplete selectedValue={commodity || ''} onSelect={onCommodityChange} />
      </Grid>
      <Grid xs={1}>
        <DeleteRow deleteCargo={deleteCargo} />
      </Grid>
    </Grid>
  )
}

export const LCLCargoRow: FC<LclCargoRowProps> = ({ index, deleteCargo }) => {
  const { field: quantityField } = useController<QuoteRequestFormState, `cargoLcl.${number}.quantity`>({
    name: `cargoLcl.${index}.quantity`,
  })
  const { field: lengthField } = useController<QuoteRequestFormState, `cargoLcl.${number}.length`>({
    name: `cargoLcl.${index}.length`,
  })
  const { field: widthField } = useController<QuoteRequestFormState, `cargoLcl.${number}.width`>({
    name: `cargoLcl.${index}.width`,
  })
  const { field: heightField } = useController<QuoteRequestFormState, `cargoLcl.${number}.height`>({
    name: `cargoLcl.${index}.height`,
  })
  const { field: grossWeightField } = useController<QuoteRequestFormState, `cargoLcl.${number}.grossWeight`>({
    name: `cargoLcl.${index}.grossWeight`,
  })
  const { field: commodityField } = useController<QuoteRequestFormState, `cargoLcl.${number}.commodityType`>({
    name: `cargoLcl.${index}.commodityType`,
  })
  const { field: volumeField } = useController<QuoteRequestFormState, `cargoLcl.${number}.volume`>({
    name: `cargoLcl.${index}.volume`,
  })

  return (
    <LeanLCLCargoRow
      deleteCargo={deleteCargo}
      quantity={quantityField.value}
      onQuantityChange={quantityField.onChange}
      length={lengthField.value}
      onLengthChange={lengthField.onChange}
      width={widthField.value}
      onWidthChange={widthField.onChange}
      height={heightField.value}
      onHeightChange={heightField.onChange}
      grossWeight={grossWeightField.value ?? 0}
      onGrossWeightChange={grossWeightField.onChange}
      commodity={commodityField.value ?? ''}
      onCommodityChange={commodityField.onChange}
      volume={volumeField.value ?? 0}
      onVolumeChange={volumeField.onChange}
    />
  )
}
